import axios, {
  AxiosError,
  type AxiosProgressEvent,
  type AxiosRequestConfig,
  type AxiosResponse,
  type Method,
} from 'axios'
import { getCurrentLocale, translate } from '@/i18n'
import { readStoredAuthSession, updateStoredAuthTokens } from '@/stores/auth.store'
import { useMessagesStore } from '@/stores/message.store'
import { env } from '@/shared/config/env'
import { apiEndpoints, buildApiPath } from '@/shared/constants/api'
import { localizeApiErrorMessage } from '@/shared/helpers/api-error'
import type { AuthSession } from '@/shared/types/auth.type'
import { useErrorStore } from '@/stores/error'

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean
    skipAuthRefresh?: boolean
    skipNotification?: boolean
    skipSuccessNotification?: boolean
    skipErrorNotification?: boolean
    skipErrorStore?: boolean
    errorNotificationMessage?: string
    successNotificationMessage?: string
    _retry?: boolean
  }
}

export interface ApiErrorPayload {
  message?: string
  error_message?: string
  error_code?: string | number
  code?: string | number
  errors?: Record<string, string[]>
}

export type ApiMessageSeverity = 'success' | 'error' | 'warning'

export class ApiClientError extends Error {
  public readonly validationMessages: string[]

  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
    public readonly errors?: Record<string, string[]>,
    public readonly isNetworkError = false,
    public readonly isTimeoutError = false,
    public readonly severity: ApiMessageSeverity = 'error',
    public readonly notificationMessage?: string,
  ) {
    super(message)
    this.name = 'ApiClientError'
    this.validationMessages = getValidationErrorMessages(errors)
  }
}

export type HttpRequestConfig = AxiosRequestConfig

export type FormPrimitive = string | number | boolean | Blob | null | undefined
export type FormValue = FormPrimitive | FormPrimitive[] | Record<string, unknown>
export type FormPayload = FormData | Record<string, FormValue>

export interface UploadFileOptions extends Omit<HttpRequestConfig, 'data' | 'method' | 'url'> {
  fieldName?: string
  fileName?: string
  data?: Record<string, FormValue>
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
}

export interface DownloadedFile {
  blob: Blob
  filename: string
}

interface AuthRefreshResponse {
  access_token?: string
  refresh_token?: string
  token?: string
  data?: {
    access_token?: string
    refresh_token?: string
    token?: string
  }
}

interface AuthTokenPair {
  access_token: string
  refresh_token?: string
}

interface ApiMessageEnvelope {
  code?: string | number
  message?: string
}

const clientRequestKey = 'HERO'
const refreshTokenUrl = buildApiPath(apiEndpoints.auth.refreshToken)
let refreshPromise: Promise<AuthTokenPair> | null = null

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    Accept: 'application/json',
  },
})

function getAccessToken(session: AuthSession | null) {
  return session?.access_token ?? session?.token
}

function getPayloadErrorCode(payload: ApiErrorPayload | undefined) {
  if (
    payload?.error_code === undefined ||
    payload.error_code === null ||
    payload.error_code === ''
  ) {
    return null
  }

  return String(payload.error_code)
}

function getPayloadSeverityCode(payload: ApiErrorPayload | undefined) {
  return payload?.error_code ?? payload?.code
}

function getErrorCode(error: AxiosError<ApiErrorPayload>, fallbackCode: string) {
  if (!error.config?.skipErrorStore) {
    useErrorStore().setError(true, getValidationErrorMessages(error.response?.data.errors))
  }
  return getPayloadErrorCode(error.response?.data) ?? fallbackCode
}

function getHttpStatusMessage(status: number) {
  const messages: Record<number, string> = {
    400: 'Bad request.',
    401: 'Authentication is required.',
    403: 'You do not have permission to perform this action.',
    404: 'Resource not found.',
    408: 'Request timeout.',
    409: 'Request conflict.',
    422: 'Validation error.',
    429: 'Too many requests. Please try again later.',
    500: 'Server error.',
    502: 'Bad gateway.',
    503: 'Service unavailable.',
    504: 'Gateway timeout.',
  }

  return messages[status] ?? `Request failed with status code ${status}.`
}

function getValidationErrorMessages(errors: ApiErrorPayload['errors']) {
  if (!errors) {
    return []
  }

  return [
    ...new Set(
      Object.values(errors)
        .flatMap((messages) => (Array.isArray(messages) ? messages : []))
        .filter((message): message is string => typeof message === 'string')
        .map((message) => localizeApiErrorMessage(message.trim()))
        .filter((message) => message !== ''),
    ),
  ]
}

function getPayloadResponseMessage(payload: ApiErrorPayload | undefined) {
  return payload?.error_message ?? payload?.message
}

function isLoginRequest(config: AxiosRequestConfig | undefined) {
  const url = String(config?.url ?? '')

  return url === '/auth/login' || url.endsWith('/auth/login')
}

function getApiMessageSeverity(
  payload: ApiErrorPayload | undefined,
  status: number,
  config: AxiosRequestConfig | undefined,
): ApiMessageSeverity {
  const numericErrorCode = Number(getPayloadSeverityCode(payload))

  if (Number.isFinite(numericErrorCode)) {
    if (numericErrorCode === 0 || numericErrorCode === 200 || numericErrorCode === 201) {
      return 'success'
    }

    if (numericErrorCode === 2) {
      return 'warning'
    }

    return 'error'
  }

  if (status === 403) {
    return 'warning'
  }

  if (status === 401 && !isLoginRequest(config)) {
    return 'warning'
  }

  return 'error'
}

function getNotificationMessage(severity: ApiMessageSeverity) {
  const key = severity === 'warning' ? 'errors.notification.warning' : 'errors.notification.error'
  const translated = translate(key)

  return translated === key ? translate('errors.unexpected') : translated
}

function isGenericLoginErrorMessage(message: string | undefined): boolean {
  if (!message) return true
  const normalized = message.trim().toLowerCase()
  return (
    normalized === 'invalid login credentials.' ||
    normalized === 'invalid login credentials' ||
    normalized === 'tên đăng nhập hoặc mật khẩu không đúng.' ||
    normalized === 'tên đăng nhập hoặc mật khẩu không đúng' ||
    normalized === 'invalid username or password.' ||
    normalized === 'invalid username or password' ||
    normalized === 'unauthenticated.' ||
    normalized === 'unauthenticated'
  )
}

function getErrorNotificationMessage(
  severity: ApiMessageSeverity,
  payload: ApiErrorPayload | undefined,
  status: number,
  config: AxiosRequestConfig | undefined,
) {
  if (config?.errorNotificationMessage) {
    return config.errorNotificationMessage
  }

  const responseMessage = getPayloadResponseMessage(payload)

  if (severity === 'warning' && responseMessage) {
    return localizeApiErrorMessage(responseMessage)
  }

  if (
    status >= 400 &&
    status < 500 &&
    responseMessage &&
    !(status === 401 && isLoginRequest(config) && isGenericLoginErrorMessage(responseMessage))
  ) {
    return localizeApiErrorMessage(responseMessage)
  }

  return getNotificationMessage(severity)
}

function getHttpStatusFallbackMessage(status: number) {
  const key = `errors.http.${status}`
  const translated = translate(key)

  return translated === key ? getHttpStatusMessage(status) : translated
}

function getResponseMessage(
  payload: ApiErrorPayload | undefined,
  status: number,
  config: AxiosRequestConfig | undefined,
) {
  const responseMessage = getPayloadResponseMessage(payload)

  if (status === 401 && isLoginRequest(config)) {
    if (isGenericLoginErrorMessage(responseMessage)) {
      return translate('auth.login.errors.invalidCredentials')
    }

    return localizeApiErrorMessage(responseMessage)
  }

  if (responseMessage) {
    return localizeApiErrorMessage(responseMessage)
  }

  return getHttpStatusFallbackMessage(status)
}

function normalizeAxiosError(error: AxiosError<ApiErrorPayload>) {
  const payload = error.response?.data

  if (!error.response) {
    const isTimeout = error.code === AxiosError.ECONNABORTED || error.code === 'ETIMEDOUT'
    const message = isTimeout ? translate('errors.timeout') : translate('errors.network')

    return new ApiClientError(
      message,
      undefined,
      isTimeout ? 'REQUEST_TIMEOUT' : 'NETWORK_ERROR',
      undefined,
      !isTimeout,
      isTimeout,
      'error',
      getNotificationMessage('error'),
    )
  }

  const status = error.response.status
  const message = getResponseMessage(payload, status, error.config)
  const severity = getApiMessageSeverity(payload, status, error.config)

  return new ApiClientError(
    message,
    status,
    getErrorCode(error, `HTTP_${status}`),
    payload?.errors,
    false,
    false,
    severity,
    getErrorNotificationMessage(severity, payload, status, error.config),
  )
}

function notifyError(error: ApiClientError, config: AxiosRequestConfig | undefined) {
  if (config?.skipNotification || config?.skipErrorNotification) {
    return
  }

  try {
    useMessagesStore().fromError(error, config?.errorNotificationMessage)
  } catch {
    // The HTTP client can be imported before Pinia is installed in tests or app bootstrap.
  }
}

function isMutationRequest(config: AxiosRequestConfig | undefined) {
  const method = String(config?.method ?? '').toLowerCase()

  return method === 'post' || method === 'put' || method === 'patch' || method === 'delete'
}

function isSuccessResponse(response: AxiosResponse<unknown>) {
  const payload = response.data as ApiMessageEnvelope | undefined
  const code = Number(payload?.code ?? response.status)

  return response.status >= 200 && response.status < 300 && code >= 200 && code < 300
}

function getSuccessNotificationMessage(response: AxiosResponse<unknown>) {
  if (response.config.successNotificationMessage) {
    return response.config.successNotificationMessage
  }

  if (!isMutationRequest(response.config) || !isSuccessResponse(response)) {
    return null
  }

  const payload = response.data as ApiMessageEnvelope | undefined

  const message = payload?.message || null
  if (!message) return null

  const translated = translate(message)
  return translated === message ? message : translated
}

function notifySuccess(response: AxiosResponse<unknown>) {
  if (response.config.skipNotification || response.config.skipSuccessNotification) {
    return
  }

  const message = getSuccessNotificationMessage(response)

  if (!message) {
    return
  }

  try {
    useMessagesStore().success(message)
  } catch {
    // The HTTP client can be imported before Pinia is installed in tests or app bootstrap.
  }
}

function extractRefreshTokens(response: AuthRefreshResponse): AuthTokenPair {
  const tokenPayload = response.data ?? response
  const accessToken = tokenPayload.access_token ?? tokenPayload.token

  if (!accessToken) {
    throw new ApiClientError(
      'Refresh token response is missing access token.',
      401,
      'TOKEN_REFRESH_FAILED',
    )
  }

  return {
    access_token: accessToken,
    refresh_token: tokenPayload.refresh_token,
  }
}

async function refreshAuthTokens(refreshToken: string) {
  if (!refreshPromise) {
    refreshPromise = apiClient
      .post<AuthRefreshResponse>(
        refreshTokenUrl,
        { refresh_token: refreshToken },
        {
          skipAuthRefresh: true,
          skipNotification: true,
        },
      )
      .then((response) => {
        const tokens = extractRefreshTokens(response.data)

        updateStoredAuthTokens(tokens)

        return tokens
      })
      .finally(() => {
        refreshPromise = null
      })
  }

  return refreshPromise
}

function appendFormValue(formData: FormData, key: string, value: FormValue) {
  if (value === null || value === undefined) {
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => appendFormValue(formData, `${key}[]`, item))
    return
  }

  if (value instanceof Blob) {
    formData.append(key, value)
    return
  }

  if (typeof value === 'object') {
    formData.append(key, JSON.stringify(value))
    return
  }

  formData.append(key, String(value))
}

function toFormData(payload: FormPayload) {
  if (payload instanceof FormData) {
    return payload
  }

  const formData = new FormData()

  Object.entries(payload).forEach(([key, value]) => appendFormValue(formData, key, value))

  return formData
}

async function request<T>(config: HttpRequestConfig) {
  const response: AxiosResponse<T> = await apiClient.request<T>(config)

  return response.data
}

function withMethod(method: Method, url: string, config?: HttpRequestConfig): HttpRequestConfig {
  return {
    ...config,
    method,
    url,
  }
}

function downloadBlob(url: string, config?: HttpRequestConfig) {
  return request<Blob>({
    ...withMethod('get', url, config),
    responseType: 'blob',
  })
}

function sanitizeDownloadedFilename(value: string) {
  const filename = value
    .replace(/^"|"$/g, '')
    .replace(/\\"/g, '"')
    .replace(/\\/g, '/')
    .split('/')
    .pop()
    ?.replace(/[\u0000-\u001f\u007f]/g, '')
    .trim()

  return filename || null
}

function getContentDispositionFilename(header: unknown) {
  if (typeof header !== 'string') {
    return null
  }

  const encodedMatch = header.match(/filename\*\s*=\s*(?:UTF-8'')?("[^"]*"|[^;]+)/i)

  if (encodedMatch?.[1]) {
    try {
      const decoded = decodeURIComponent(encodedMatch[1].replace(/^"|"$/g, ''))
      const filename = sanitizeDownloadedFilename(decoded)

      if (filename) {
        return filename
      }
    } catch {
      // Fall back to the regular filename parameter below.
    }
  }

  const fallbackMatch = header.match(/(?:^|;)\s*filename\s*=\s*("(?:[^"\\]|\\.)*"|[^;]+)/i)

  return fallbackMatch?.[1] ? sanitizeDownloadedFilename(fallbackMatch[1]) : null
}

async function downloadFile(url: string, config?: HttpRequestConfig): Promise<DownloadedFile> {
  const response = await apiClient.request<Blob>({
    ...withMethod('get', url, config),
    responseType: 'blob',
  })
  const filename = getContentDispositionFilename(response.headers['content-disposition'])

  if (!filename) {
    throw new Error('The download response is missing a valid Content-Disposition filename.')
  }

  return {
    blob: response.data,
    filename,
  }
}

apiClient.interceptors.request.use((config) => {
  const session = readStoredAuthSession()
  const accessToken = getAccessToken(session)

  config.headers = config.headers ?? {}
  config.headers['X-CLIENT-LANGUAGE'] = getCurrentLocale()
  config.headers['X-CLIENT-REQUEST'] = clientRequestKey

  if (!config.skipAuth && accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }

  if (!config.skipAuth && !config.headers['X-Tenant-Code'] && session?.tenant?.code) {
    config.headers['X-Tenant-Code'] = session.tenant.code
  }

  if (!config.skipAuth && !config.headers['X-Branch-Id'] && session?.currentBranchId) {
    config.headers['X-Branch-Id'] = String(session.currentBranchId)
  }

  return config
})

apiClient.interceptors.response.use(
  (response) => {
    notifySuccess(response)

    return response
  },
  async (error: AxiosError<ApiErrorPayload>) => {
    const originalConfig = error.config
    const session = readStoredAuthSession()

    if (
      error.response?.status === 401 &&
      originalConfig &&
      !originalConfig._retry &&
      !originalConfig.skipAuthRefresh &&
      session?.refresh_token
    ) {
      originalConfig._retry = true

      let tokens: AuthTokenPair

      try {
        tokens = await refreshAuthTokens(session.refresh_token)
      } catch {
        const normalizedOriginalError = normalizeAxiosError(error)

        notifyError(normalizedOriginalError, originalConfig)
        throw normalizedOriginalError
      }

      originalConfig.headers = originalConfig.headers ?? {}
      originalConfig.headers.Authorization = `Bearer ${tokens.access_token}`

      return apiClient.request(originalConfig)
    }

    const normalizedError = normalizeAxiosError(error)

    notifyError(normalizedError, originalConfig)

    throw normalizedError
  },
)

export const http = {
  request,

  get<T>(url: string, config?: HttpRequestConfig) {
    return request<T>(withMethod('get', url, config))
  },

  delete<T>(url: string, config?: HttpRequestConfig) {
    return request<T>(withMethod('delete', url, config))
  },

  post<T, D = unknown>(url: string, data?: D, config?: HttpRequestConfig) {
    return request<T>({
      ...withMethod('post', url, config),
      data,
    })
  },

  put<T, D = unknown>(url: string, data?: D, config?: HttpRequestConfig) {
    return request<T>({
      ...withMethod('put', url, config),
      data,
    })
  },

  patch<T, D = unknown>(url: string, data?: D, config?: HttpRequestConfig) {
    return request<T>({
      ...withMethod('patch', url, config),
      data,
    })
  },

  downloadBlob,
  downloadFile,

  submitForm<T>(url: string, payload: FormPayload, config?: HttpRequestConfig) {
    return request<T>({
      ...withMethod('post', url, config),
      data: toFormData(payload),
    })
  },

  uploadFile<T>(url: string, file: Blob, options: UploadFileOptions = {}) {
    const { fieldName = 'file', fileName, data, ...config } = options
    const formData = toFormData(data ?? {})

    if (fileName) {
      formData.append(fieldName, file, fileName)
    } else {
      formData.append(fieldName, file)
    }

    return request<T>({
      ...withMethod('post', url, config),
      data: formData,
    })
  },
}
